// Third-party
import React, { ReactNode, CSSProperties } from 'react';
import classNames from 'classnames';
import throttle from 'lodash/throttle';

// Components
import { GenericScrollbarContainer } from '../../../components/misc';

type Props = {
  children: ReactNode;
  extraStyles?: CSSProperties;
  className?: string;
  isEditPinned: boolean;
  withPadding?: boolean;
  onScrollX?: (atStart?: boolean) => void;
  onScrollY?: () => void;
};

type State = {
  arrowsFixed: boolean;
  rightElement?: Element;
  leftElement?: Element;
};

class ComparisonScrollbarContainer extends React.Component<Props, State> {
  private tablePaddingRef: HTMLElement | null = null;

  private genericScrollbarContainerRef: GenericScrollbarContainer | null = null;

  private childrenRef: HTMLElement | null = null;

  readonly state: State = {
    arrowsFixed: false,
  };

  adjustArrowPosition = throttle(() => {
    const { arrowsFixed: prevArrowsFixed } = this.state;
    const scrollContainerRef = this.getScrollContainerRef();

    const arrow = document.querySelector('.arrow-navigation.generic-scrollbar-arrows:not(.disabled)') as HTMLDivElement;

    if (!scrollContainerRef || !arrow) {
      return;
    }

    const { parentElement } = scrollContainerRef;

    if (!parentElement) {
      return;
    }

    let arrowsFixed = false;
    const arrowTopOffset = arrow.getBoundingClientRect().top;

    if (!prevArrowsFixed) {
      const threshold = window.innerHeight / 2 - 12;
      arrowsFixed = arrowTopOffset <= threshold;
    } else {
      const parentTopOffset = parentElement.getBoundingClientRect().top;

      const diff = arrowTopOffset - parentTopOffset;
      arrowsFixed = diff > 100;
    }

    if (arrowsFixed !== this.state.arrowsFixed) {
      this.setState({ arrowsFixed });
    }
  }, 100);

  componentDidMount() {
    this.setRightElement();
    this.setLeftElement();
  }

  componentDidUpdate(prevProps: Props) {
    const { isEditPinned, children } = this.props;
    const ref = this.genericScrollbarContainerRef;

    if (children !== prevProps.children && ref) {
      this.setRightElement();
      this.setLeftElement();
      setTimeout(() => ref.adjustArrows(), 1000);
    }

    if (prevProps.isEditPinned !== isEditPinned && ref) {
      const scrollbar = document.getElementsByClassName('app-scrollbar-container')[0] as HTMLDivElement;

      setTimeout(() => ref.adjustArrows(), 1000);

      if (!isEditPinned) {
        ref.handleResize();
        scrollbar.removeEventListener('scroll', this.onScroll);
      } else {
        scrollbar.addEventListener('scroll', this.onScroll);
      }
    }
  }

  getScrollContainerRef = () => (this.genericScrollbarContainerRef ? this.genericScrollbarContainerRef.getScrollContainerRef() : undefined);

  getPaddingRef = () => this.tablePaddingRef;

  onScroll = () => {
    this.adjustArrowPosition();
  };

  refreshStickyHeaders = () => {
    // react-sticky only refreshes sticky elements based on specific
    // DOM events - `scroll` and `resize` being some of them. Since a
    // lot of stuff in the app listens to `resize`, we'll use `scroll` here
    // to update the headers. A bit hacky, but saves a ton on performance.
    requestAnimationFrame(() => {
      window.dispatchEvent(new Event('scroll'));
    });
  };

  onResize = () => {
    this.adjustArrowPosition();
    this.refreshStickyHeaders();

    // Refresh twice - the 2nd time acts as a safety-net as sometimes
    // the headers seem to refresh before the table itself has refreshed,
    // setting them to the wrong size.
    setTimeout(() => {
      this.refreshStickyHeaders();
    }, 2000);
  };

  setRightElement = () => {
    const { withPadding } = this.props;

    if (!this.childrenRef) {
      return;
    }

    const { lastElementChild } = this.childrenRef;

    if (!lastElementChild || !lastElementChild.previousElementSibling) {
      return;
    }

    const rightElement = withPadding && lastElementChild ? lastElementChild.previousElementSibling : lastElementChild;
    this.setState({ rightElement });
  };

  setLeftElement = () => {
    if (!this.childrenRef || !this.childrenRef.firstElementChild) {
      return;
    }

    this.setState({ leftElement: this.childrenRef.firstElementChild });
  };

  onScrollX = (atStart?: boolean) => {
    if (this.props.onScrollX) {
      this.props.onScrollX(atStart);
    }

    this.refreshStickyHeaders();
  };

  onScrollY = () => {
    if (this.props.onScrollY) {
      this.props.onScrollY();
    }
  };

  render() {
    const { className, children, extraStyles, isEditPinned, withPadding } = this.props;

    const { arrowsFixed, rightElement, leftElement } = this.state;

    const arrowLeftClasses = classNames({
      stick: arrowsFixed,
    });

    const arrowRightClasses = classNames({
      stick: arrowsFixed && !isEditPinned,
      'stick-edit-open': arrowsFixed && isEditPinned,
    });

    return (
      <GenericScrollbarContainer
        className={classNames('comparison-scrollbar-container', className)}
        extraStyles={extraStyles}
        extraLeftArrowClasses={arrowLeftClasses}
        extraRightArrowClasses={arrowRightClasses}
        ref={ref => {
          this.genericScrollbarContainerRef = ref;
        }}
        onScroll={this.onScroll}
        onResize={this.onResize}
        onScrollX={this.onScrollX}
        onScrollY={this.onScrollY}
        boundingRightElement={rightElement}
        boundingLeftElement={leftElement}
        role="rowgroup"
      >
        <div
          className="children-container"
          ref={ref => {
            this.childrenRef = ref;
          }}
        >
          {children}

          {withPadding && (
            <div
              className="padding"
              ref={ref => {
                this.tablePaddingRef = ref;
              }}
            />
          )}
        </div>
      </GenericScrollbarContainer>
    );
  }
}

export default ComparisonScrollbarContainer;
